import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext.js'
import { useLocale } from '../locale/LocaleContext.js'
import { Avatar } from './Avatar.js'
import { MountainIcon, UserIcon } from './icons/GlyphIcons.js'
import { api } from '../api/client.js'

// AppShell — global chrome. Desktop keeps brand / tabs / account in one
// fixed-height row; mobile keeps brand and tabs split across two compact rows.

type IconComponent = (props: { width?: number; height?: number }) => JSX.Element

interface RailTab {
  /** Stable id used to build the rail/mobile testids (`tab-<id>` / `mobile-tab-<id>`). */
  id: string
  to: string
  label: string
  Icon: IconComponent
  /** Active based on the current pathname + search (activity tabs share `/discover`
   *  and differ only by the `activity` query param). */
  isActive: (loc: { pathname: string; search: string }) => boolean
  badgeCount?: number | null
}

function isDiscoverPath(pathname: string): boolean {
  return pathname === '/' || pathname.startsWith('/discover')
}

export function AppShell({ children }: { children: ReactNode }) {
  const auth = useAuth()
  const { t } = useLocale()
  const location = useLocation()
  const [unreadCount, setUnreadCount] = useState<number | null>(null)

  useEffect(() => {
    if (auth.status !== 'authed' || !auth.token || !auth.user) {
      setUnreadCount(null)
      return
    }
    setUnreadCount(null)
    let cancelled = false
    const token = auth.token
    const userId = auth.user.id
    const load = () => {
      void api.getInboxUnreadCount(token).then(
        (count) => {
          if (!cancelled && auth.user?.id === userId) setUnreadCount(count)
        },
        () => {
          if (!cancelled) setUnreadCount(null)
        },
      )
    }
    load()
    window.addEventListener('xr:inbox-changed', load)
    return () => {
      cancelled = true
      window.removeEventListener('xr:inbox-changed', load)
    }
  }, [auth.status, auth.token, auth.user?.id])

  const tabs: RailTab[] = [
    {
      id: 'discover',
      to: '/discover',
      label: t('navDiscover'),
      Icon: MountainIcon,
      isActive: (loc) => isDiscoverPath(loc.pathname),
    },
    {
      id: 'me',
      to: unreadCount && unreadCount > 0 ? '/me?tab=collab' : '/me',
      label: t('navMe'),
      Icon: UserIcon,
      isActive: (loc) => loc.pathname.startsWith('/me'),
      badgeCount: unreadCount,
    },
  ]
  if (auth.status === 'authed' && auth.user?.role === 'admin') {
    tabs.push({
      id: 'admin',
      to: '/admin',
      label: t('admin.portal'),
      Icon: UserIcon,
      isActive: (loc) => loc.pathname === '/admin' || loc.pathname.startsWith('/admin/'),
    })
  }

  return (
    <div className="flex min-h-[100dvh] w-full flex-col bg-[var(--xr-bg-canvas)] text-[var(--xr-text-primary)] lg:h-[100dvh] lg:min-h-0 lg:overflow-hidden">
      <header className="sticky top-0 z-30 flex-none border-b border-[var(--xr-border-default)] md:relative">
        <div className="relative flex h-[38px] items-center gap-2 bg-[#1a5a44] px-4 md:h-14">
          <Link to="/" aria-label={t('appName')} className="flex items-center gap-1.5 flex-none">
            <img src="/xingroute-mark-mono-white.svg" alt="" className="h-6 w-6" />
            <span className="text-sm font-semibold text-white">寻径</span>
          </Link>
          <span className="flex-1" />
          {auth.status === 'authed' && (
            <Link
              to="/routes/new"
              className="inline-flex h-[26px] flex-none items-center rounded-[var(--xr-radius-md)] border border-white/45 px-2.5 text-[11px] font-medium text-white/90 transition hover:border-white hover:text-white"
              data-testid="create-new-link"
            >
              {t('navCreate')}
            </Link>
          )}
          <AccountControl />
        </div>

        <nav
          aria-label={t('mainNavigation')}
          className="flex h-[34px] items-stretch justify-center border-b border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] md:absolute md:left-1/2 md:top-0 md:h-14 md:-translate-x-1/2 md:border-b-0 md:bg-transparent"
          data-testid="global-nav"
        >
          {tabs.map((tab) => (
            <ShellTab key={tab.id} tab={tab} location={location} />
          ))}
        </nav>
      </header>

      {/* Page content + ICP footer */}
      <main className="relative flex min-w-0 flex-1 flex-col lg:min-h-0 lg:overflow-hidden">
        {children}
      </main>

      <SiteFooter />
    </div>
  )
}

function ShellTab({
  tab,
  location,
}: {
  tab: RailTab
  location: { pathname: string; search: string }
}) {
  const active = tab.isActive(location)
  return (
    <Link
      to={tab.to}
      className={
        'flex items-center justify-center border-b-2 px-6 text-[12px] font-medium transition md:px-7 ' +
        (active
          ? 'border-[var(--xr-brand-primary)] text-[var(--xr-brand-primary)] md:border-white md:text-white'
          : 'border-transparent text-[var(--xr-text-muted)] hover:text-[var(--xr-text-primary)] md:text-white/72 md:hover:text-white')
      }
      data-testid={`tab-${tab.id}`}
    >
      {tab.label}
      {tab.badgeCount !== null && tab.badgeCount !== undefined && tab.badgeCount > 0 && (
        <span
          className="ml-1.5 min-w-4 rounded-full bg-[var(--xr-danger)] px-1 text-center text-[10px] leading-4 text-white"
          aria-label={String(tab.badgeCount)}
          data-testid={`tab-${tab.id}-badge`}
        >
          {tab.badgeCount > 99 ? '99+' : tab.badgeCount}
        </span>
      )}
    </Link>
  )
}

// AccountControl — authed avatar button + dropdown, or a sign-in affordance.
function AccountControl() {
  const auth = useAuth()
  const { t, locale } = useLocale()
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const avatarName =
    auth.status === 'authed' ? auth.user?.display_name || auth.user?.username || '' : ''

  if (auth.status !== 'authed') {
    return (
      <Link
        to="/signin"
        className="inline-flex h-[26px] flex-none items-center rounded-[var(--xr-radius-md)] border border-white/60 px-2.5 text-[11px] font-medium text-white/90 hover:border-white hover:text-white"
      >
        {t('signIn')}
      </Link>
    )
  }

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        aria-label={t('account')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex-none rounded-full transition hover:brightness-105"
        data-testid="topbar-avatar"
      >
        <Avatar
          avatarUrl={auth.user?.avatar_url}
          seed={avatarName}
          displayName={avatarName}
          sizeClass="h-8 w-8 text-xs"
          locale={locale}
        />
      </button>

      <AvatarMenu
        open={open}
        onClose={close}
        anchorRef={btnRef}
        onSignOut={() => void auth.logout()}
      />
    </div>
  )
}

// SiteFooter — ICP filing strip mandated by MIIT 《非经营性互联网信息服务备案
// 管理办法》: every page renders the per-site filing number with a deep link to
// the official beian portal so inspectors and visitors can verify the record.
// Single compact line per user direction (2026-06-13): ICP filing number +
// map-boundary disclaimer separated by a `｜` (fullwidth pipe). Mapbox /
// OpenStreetMap attribution is no longer rendered here; it lives on each
// interactive map (AttributionControl) and inside the Static Images output
// for thumbnails (see wiki/concepts/mapbox-attribution.md).
const ICP_FILING_NUMBER = '苏ICP备2026038534号-1'
const ICP_BEIAN_URL = 'https://beian.miit.gov.cn'

function SiteFooter() {
  const { t } = useLocale()
  return (
    <footer
      className="flex min-h-10 flex-none flex-wrap items-center justify-center gap-x-2 gap-y-1 border-t border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-3 py-2 text-center text-[11px] leading-tight text-[var(--xr-text-muted)]"
      data-testid="site-icp-footer"
    >
      <a
        href={ICP_BEIAN_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('footer.icpAriaLabel')}
        className="font-mono tracking-tight transition hover:text-[var(--xr-text-primary)]"
      >
        {ICP_FILING_NUMBER}
      </a>
      <span aria-hidden>｜</span>
      <span
        aria-label={t('footer.complianceAriaLabel')}
        data-testid="site-compliance-disclaimer"
        className="tracking-tight"
      >
        {t('footer.complianceDisclaimer')}
      </span>
      <nav
        aria-label={t('footer.linksLabel')}
        className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1"
      >
        <Link to="/privacy" className="transition hover:text-[var(--xr-text-primary)]">
          {t('support.privacy')}
        </Link>
        <Link to="/terms" className="transition hover:text-[var(--xr-text-primary)]">
          {t('support.terms')}
        </Link>
        <Link to="/support" className="transition hover:text-[var(--xr-text-primary)]">
          {t('support.title')}
        </Link>
      </nav>
    </footer>
  )
}

function AvatarMenu({
  open,
  onClose,
  anchorRef,
  onSignOut,
}: {
  open: boolean
  onClose: () => void
  anchorRef: RefObject<HTMLElement | null>
  onSignOut: () => void
}) {
  const { t } = useLocale()
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      const target = e.target as Node
      if (ref.current?.contains(target)) return
      if (anchorRef.current?.contains(target)) return
      onClose()
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose, anchorRef])

  if (!open) return null

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={t('account')}
      data-testid="avatar-menu"
      className="absolute z-50 w-44 rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-2 text-sm shadow-[var(--xr-shadow-md)] right-0 top-full mt-2"
    >
      <Link
        to="/me/settings"
        role="menuitem"
        onClick={onClose}
        className="block rounded-[var(--xr-radius-sm)] px-3 py-2 text-[var(--xr-text-primary)] hover:bg-[var(--xr-bg-surface-elev)]"
      >
        {t('settings')}
      </Link>
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onClose()
          onSignOut()
        }}
        className="block w-full rounded-[var(--xr-radius-sm)] px-3 py-2 text-left text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]"
      >
        {t('signOut')}
      </button>
    </div>
  )
}
